// Shadcn-style Checkbox Stimulus controller
// Provides enhanced checkbox behavior with custom styling and interactions
// Handles visual state synchronization and accessibility

import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['visual']
  static values = {
    name: String,
    checked: Boolean
  }

  connect() {
    // Initialize visual state based on actual checkbox
    this.initializeVisualState()

    // Set up click delegation for the entire checkbox area
    this.element.addEventListener('click', this.handleClick.bind(this))

    // Handle keyboard navigation
    this.element.addEventListener('keydown', this.handleKeydown.bind(this))

    // Mark as JS-enabled for styling
    document.documentElement.classList.add('js-enabled')
  }

  disconnect() {
    this.element.removeEventListener('click', this.handleClick.bind(this))
    this.element.removeEventListener('keydown', this.handleKeydown.bind(this))
  }

  // Handle clicks anywhere in the checkbox area
  handleClick(event) {
    const checkbox = this.getCheckboxInput()

    // Don't double-handle if click was directly on the input
    if (event.target === checkbox) {
      return
    }

    // Don't handle if disabled
    if (checkbox.disabled) {
      return
    }

    // Prevent default to avoid double-firing
    event.preventDefault()

    // Toggle the checkbox
    this.toggle()
  }

  // Handle keyboard interaction
  handleKeydown(event) {
    const checkbox = this.getCheckboxInput()

    // Only handle if focus is on our element or the input
    if (!this.element.contains(event.target)) {
      return
    }

    // Handle spacebar
    if (event.key === ' ' || event.code === 'Space') {
      event.preventDefault()

      if (!checkbox.disabled) {
        this.toggle()
      }
    }
  }

  // Toggle checkbox state
  toggle() {
    const checkbox = this.getCheckboxInput()

    if (checkbox.disabled) {
      return
    }

    // Update the actual checkbox
    checkbox.checked = !checkbox.checked

    // Update our value
    this.checkedValue = checkbox.checked

    // Sync visual state
    this.updateVisualState()

    // Dispatch change event for form handling
    checkbox.dispatchEvent(new Event('change', { bubbles: true }))

    // Dispatch custom event for other components
    this.dispatch('change', {
      detail: {
        name: this.nameValue,
        checked: checkbox.checked,
        value: checkbox.value
      }
    })
  }

  // Update visual state when checked value changes
  checkedValueChanged() {
    this.updateVisualState()
  }

  // Initialize visual state on connect
  initializeVisualState() {
    const checkbox = this.getCheckboxInput()

    if (checkbox) {
      this.checkedValue = checkbox.checked
      this.updateVisualState()
    }
  }

  // Update visual styling based on current state
  updateVisualState() {
    const checkbox = this.getCheckboxInput()
    const visual = this.getVisualElement()

    if (!checkbox || !visual) {
      return
    }

    // Update visual element classes based on checked state
    if (checkbox.checked) {
      visual.classList.add('peer-checked:bg-orange-600', 'peer-checked:border-orange-600')
      this.showCheckIcon(visual)
    } else {
      visual.classList.remove('peer-checked:bg-orange-600', 'peer-checked:border-orange-600')
      this.hideCheckIcon(visual)
    }

    // Update ARIA attributes
    checkbox.setAttribute('aria-checked', checkbox.checked.toString())
  }

  // Show check icon
  showCheckIcon(visual) {
    let icon = visual.querySelector('[data-check-icon]')

    if (!icon) {
      icon = this.createCheckIcon()
      visual.appendChild(icon)
    }

    icon.style.display = 'block'
  }

  // Hide check icon
  hideCheckIcon(visual) {
    const icon = visual.querySelector('[data-check-icon]')

    if (icon) {
      icon.style.display = 'none'
    }
  }

  // Create check icon element
  createCheckIcon() {
    const icon = document.createElement('svg')
    icon.setAttribute('data-check-icon', 'true')
    icon.className = 'h-3 w-3 text-white'
    icon.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
    icon.setAttribute('viewBox', '0 0 20 20')
    icon.setAttribute('fill', 'currentColor')
    icon.setAttribute('aria-hidden', 'true')

    const path = document.createElement('path')
    path.setAttribute('fill-rule', 'evenodd')
    path.setAttribute('d', 'M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z')
    path.setAttribute('clip-rule', 'evenodd')

    icon.appendChild(path)
    return icon
  }

  // Get the actual checkbox input
  getCheckboxInput() {
    return this.element.querySelector('input[type="checkbox"]')
  }

  // Get the visual element
  getVisualElement() {
    return this.element.querySelector('[data-checkbox-visual]')
  }

  // Handle form reset
  handleReset() {
    // Reset to original state
    const checkbox = this.getCheckboxInput()

    if (checkbox) {
      // Reset checkbox to its default state
      checkbox.checked = checkbox.defaultChecked
      this.checkedValue = checkbox.checked
      this.updateVisualState()
    }
  }
}